import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { format, parseISO } from "date-fns"
import { Loader2 } from "lucide-react"
import { useState } from "preact/hooks"
import { api_fetch } from "../api/client.tsx"

interface AutoValidationLastRun {
  at: string | null
  validated_count: number | null
  message: string | null
}

interface AutoValidationStatus {
  spoilage_auto_validate: boolean
  contractual_auto_validate: boolean
  auto_validate_threshold: number | null
  eligible_count: number
  next_scheduled_run_at: string | null
  schedule_enabled: boolean
  last_run: AutoValidationLastRun
}

interface AutoValidateResponse {
  validated_count: number
}

export function AutoValidationSettings() {
  const [settings, setSettings] = useState<AutoValidationStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [thresholdInput, setThresholdInput] = useState<string>("")
  const [confirmOpen, setConfirmOpen] = useState(false)

  useAsyncEffect(async () => {
    setIsLoading(true)
    try {
      const res = await api_fetch<AutoValidationStatus>("/organization/auto_validation")

      if (res.ok) {
        const data = res.value.data
        setSettings(data)
        setThresholdInput(data.auto_validate_threshold?.toString() || "")
      } else {
        toast({
          title: "Error",
          description: "Failed to load auto-validation settings",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Failed to fetch auto-validation settings:", error)
      toast({
        title: "Error",
        description: "Failed to load auto-validation settings",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  async function reloadSettings() {
    const res = await api_fetch<AutoValidationStatus>("/organization/auto_validation")
    if (res.ok) {
      setSettings(res.value.data)
      setThresholdInput(res.value.data.auto_validate_threshold?.toString() || "")
    }
  }

  async function handleSave() {
    if (!settings) return

    setIsSaving(true)
    try {
      const thresholdValue = thresholdInput.trim() === "" ? null : parseFloat(thresholdInput)

      if (thresholdInput.trim() !== "" && (isNaN(thresholdValue!) || thresholdValue! <= 0)) {
        toast({
          title: "Invalid Input",
          description: "Amount threshold must be a positive number",
          variant: "destructive",
        })
        setIsSaving(false)
        return
      }

      const payload = {
        spoilage_auto_validate: settings.spoilage_auto_validate,
        contractual_auto_validate: settings.contractual_auto_validate,
        auto_validate_threshold: thresholdValue,
      }

      const res = await api_fetch<AutoValidateResponse>("/organization/auto_validation", {
        method: "POST",
        body: payload,
      })

      if (res.ok) {
        const validatedCount = res.value.data.validated_count
        toast({
          title: "Settings saved",
          description: `${validatedCount} deduction${validatedCount !== 1 ? "s" : ""} ${validatedCount === 1 ? "was" : "were"} validated immediately.`,
        })
        await reloadSettings()
      } else {
        toast({
          title: "Error",
          description: res.error?.message || "Failed to update auto-validation settings",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Failed to update auto-validation settings:", error)
      toast({
        title: "Error",
        description: "Failed to update auto-validation settings. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
      setConfirmOpen(false)
    }
  }

  function onSaveClick() {
    if (!settings) return
    const willValidate = settings.eligible_count > 0
    if (willValidate) {
      setConfirmOpen(true)
      return
    }
    void handleSave()
  }

  function handleSpoilageChange(checked: boolean) {
    if (!settings) return
    setSettings({ ...settings, spoilage_auto_validate: checked })
  }

  function handleContractualChange(checked: boolean) {
    if (!settings) return
    setSettings({ ...settings, contractual_auto_validate: checked })
  }

  if (isLoading) {
    return <div class="text-gray-500">Loading auto-validation settings...</div>
  }

  if (!settings) {
    return <div class="text-gray-500">Failed to load settings</div>
  }

  const saveWillValidate = settings.eligible_count > 0

  return (
    <div class="space-y-6">
      <div class="rounded-md border border-gray-200 bg-gray-50/80 p-4 max-w-2xl space-y-3">
        <p class="text-sm font-medium text-gray-800">Auto-validation status</p>
        <p class="text-sm text-gray-800">
          <span class="font-semibold tabular-nums">{settings.eligible_count}</span> deduction
          {settings.eligible_count === 1 ? "" : "s"} currently match your rules and are still{" "}
          <span class="font-medium">new</span>.
        </p>
        <p class="text-xs text-gray-500">
          {settings.schedule_enabled
            ? "Nightly run at 2:00 AM UTC validates matching new deductions."
            : "No nightly schedule — enable at least one rule below."}
        </p>
        <p class="text-xs text-gray-500">
          Next scheduled run:{" "}
          {settings.next_scheduled_run_at
            ? format(parseISO(settings.next_scheduled_run_at), "MMM d, yyyy 'at' h:mm a")
            : "—"}
        </p>
        {settings.last_run.at != null && settings.last_run.at !== "" && (
          <p class="text-xs text-gray-600 border-t border-gray-200 pt-3">
            <span class="font-medium text-gray-800">Last run </span>
            {format(parseISO(settings.last_run.at), "MMM d, yyyy 'at' h:mm a")}
            {settings.last_run.validated_count != null
              ? ` — validated ${settings.last_run.validated_count}.`
              : "."}
          </p>
        )}
        {settings.last_run.message && (
          <p class="text-xs text-gray-600">{settings.last_run.message}</p>
        )}
      </div>

      <div class="space-y-4">
        <div class="flex items-center space-x-2">
          <Checkbox
            id="spoilage"
            checked={settings.spoilage_auto_validate}
            onCheckedChange={(checked: any) => handleSpoilageChange(!!checked)}
          />
          <Label htmlFor="spoilage" class="cursor-pointer">
            Auto-validate spoilage deductions
          </Label>
        </div>

        <div class="flex items-center space-x-2">
          <Checkbox
            id="contractual"
            checked={settings.contractual_auto_validate}
            onCheckedChange={(checked: any) => handleContractualChange(!!checked)}
          />
          <Label htmlFor="contractual" class="cursor-pointer">
            Auto-validate contractual deductions
          </Label>
        </div>
        <p class="text-sm text-gray-500 -mt-2">
          Contractual includes fair share and merchandising categories.
        </p>

        <div class="space-y-2">
          <Label htmlFor="threshold">Amount threshold (optional)</Label>
          <Input
            id="threshold"
            type="number"
            step="0.01"
            min="0"
            placeholder="e.g., 50.00"
            value={thresholdInput}
            onInput={(e: any) => setThresholdInput(e.target.value)}
          />
          <p class="text-sm text-gray-500">
            Deductions at or below this amount (in dollars) will be automatically validated, regardless of category.
          </p>
        </div>
      </div>

      <div
        class={
          saveWillValidate
            ? "rounded-md border border-amber-300 bg-amber-50 p-4 max-w-2xl space-y-2"
            : "rounded-md border border-gray-200 bg-gray-50/80 p-4 max-w-2xl"
        }
      >
        <p class={saveWillValidate ? "text-sm font-medium text-amber-900" : "text-sm text-gray-600"}>
          {saveWillValidate ? (
            <>
              Saving will <span class="font-semibold">immediately validate</span> all{" "}
              <span class="font-semibold tabular-nums">{settings.eligible_count}</span> matching deduction
              {settings.eligible_count === 1 ? "" : "s"} — not only on the nightly schedule.
            </>
          ) : (
            <>Saving updates your rules. No matching new deductions will be validated right now.</>
          )}
        </p>
      </div>

      <div class="flex justify-end">
        <Button
          onClick={onSaveClick}
          disabled={isSaving}
          variant={saveWillValidate ? "default" : "outline"}
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving…
            </>
          ) : saveWillValidate ? (
            `Save & validate ${settings.eligible_count} deduction${settings.eligible_count === 1 ? "" : "s"}`
          ) : (
            "Save settings"
          )}
        </Button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Validate deductions now?</AlertDialogTitle>
            <AlertDialogDescription>
              Saving these settings will immediately mark{" "}
              <span class="font-semibold">{settings.eligible_count}</span> matching deduction
              {settings.eligible_count === 1 ? "" : "s"} as validated. This runs right away in addition to any
              nightly schedule. Review the count before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={isSaving} onClick={() => void handleSave()}>
              {isSaving ? "Saving…" : `Save & validate ${settings.eligible_count}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
